import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, fmtDate } from '../../components/ui';
import { Kpis, Panel } from '../../components/charts';

/* ============================================================
   Papan mingguan.

   Minggunya datang dari ?week=, bukan dari jam browser. Laporan
   minggu lalu harus bisa dibuka ulang minggu depan dan menunjukkan
   angka yang sama; papan yang menghitung "hari ini" sendiri tidak
   bisa dikirim ke orang lain sebagai bukti apa pun.

   Bagian tanpa nama bukan sisa yang disembunyikan di bawah. Ia
   pertanyaan yang sebenarnya: aktivitas yang berjalan minggu ini
   dan tidak ada orang yang bisa ditanyai tentangnya.
   ============================================================ */

export type WeekActivity = {
  activityId: string;
  code: string;
  name: string;
  start: string | null;
  finish: string | null;
  location: string | null;
  isCritical: boolean;
  pctComplete: number;
  late: boolean;
};

type PersonWeek = {
  personId: string;
  name: string;
  org: string | null;
  role: string | null;
  activities: WeekActivity[];
  load: number;
  lateCount: number;
};

type Board = {
  from: string;
  to: string;
  people: PersonWeek[];
  unassigned: WeekActivity[];
  totals: {
    people: number;
    activities: number;
    assigned: number;
    unassigned: number;
    overloaded: number;
    coveragePct: number | null;
  };
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

export const fmtInt = (v: number): string => v.toLocaleString('id-ID');

/**
 * Tanggal hari ini menurut jam dinding setempat.
 *
 * toISOString() memakai UTC, dan di WIB itu berarti Senin pagi
 * sebelum pukul tujuh masih terbaca hari Minggu — yaitu minggu
 * yang salah, tepat pada jam orang membuka papan ini.
 */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
}

/** Cermin weekRange(iso, 'MON') di calendar-core.ts. */
export function mondayOf(iso: string): string {
  if (!DATE.test(iso)) return mondayOf(todayIso());
  const ms = Date.parse(`${iso}T00:00:00Z`);
  const back = (new Date(ms).getUTCDay() + 6) % 7;
  return new Date(ms - back * MS_PER_DAY).toISOString().slice(0, 10);
}

export function shiftWeek(iso: string, weeks: number): string {
  return new Date(Date.parse(`${mondayOf(iso)}T00:00:00Z`) + weeks * 7 * MS_PER_DAY)
    .toISOString()
    .slice(0, 10);
}

/**
 * Di pengembangan modul ditentukan oleh ?module=, jadi query yang
 * sudah ada harus ikut terbawa. Menimpanya akan memindahkan orang
 * ke modul lain begitu halaman dimuat ulang.
 */
export function withParams(
  path: string,
  params: URLSearchParams,
  extra: Record<string, string> = {},
): string {
  const p = new URLSearchParams(params);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const q = p.toString();
  return q ? `${path}?${q}` : path;
}

function Activities({ rows }: { rows: WeekActivity[] }) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Kode</th>
            <th>Aktivitas</th>
            <th>Lokasi</th>
            <th>Mulai</th>
            <th>Selesai</th>
            <th>Selesai menurut jadwal</th>
            <th>Catatan</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.activityId} className="no-click">
              <td className="num">{a.code}</td>
              <td>{a.name}</td>
              <td>{a.location ?? '—'}</td>
              <td>{fmtDate(a.start)}</td>
              <td className={a.late ? 'neg' : ''}>{fmtDate(a.finish)}</td>
              <td>{a.pctComplete.toFixed(0)}%</td>
              <td>
                {a.isCritical && <span className="chip crit">Kritis</span>}
                {a.late && <span className="chip warn">Lewat tanggal</span>}
                {!a.isCritical && !a.late && '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Week({ projectId }: { projectId: string }) {
  const [params, setParams] = useSearchParams();
  const week = mondayOf(params.get('week') ?? todayIso());

  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const b = await api.get<Board>(`/team/week?projectId=${projectId}&week=${week}`);
      setBoard(b);
      setOpen(b.people[0]?.personId ?? null);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the week board.');
    }
  }, [projectId, week]);

  useEffect(() => {
    void load();
  }, [load]);

  function goto(iso: string) {
    const p = new URLSearchParams(params);
    p.set('week', mondayOf(iso));
    setParams(p, { replace: true });
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!board) return <Loading rows={5} />;

  const t = board.totals;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Papan mingguan</h1>
          <p>
            {fmtDate(board.from)} – {fmtDate(board.to)}. Aktivitas yang jendelanya berpotongan dengan
            minggu ini, bukan hanya yang mulai di dalamnya.
          </p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => goto(shiftWeek(week, -1))}>
            Minggu sebelumnya
          </button>
          <input
            className="input"
            type="date"
            value={week}
            onChange={(e) => e.target.value && goto(e.target.value)}
            title="Tanggal mana pun dalam minggu yang dituju"
          />
          <button className="btn btn-ghost btn-sm" onClick={() => goto(shiftWeek(week, 1))}>
            Minggu berikutnya
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => goto(todayIso())}>
            Minggu ini
          </button>
        </div>
      </div>

      <Kpis
        items={[
          { label: 'Aktivitas minggu ini', value: fmtInt(t.activities) },
          {
            label: 'Ada namanya',
            value: fmtInt(t.assigned),
            note: t.coveragePct == null ? 'belum terukur' : `${t.coveragePct}% cakupan`,
            tone: t.coveragePct == null ? undefined : t.coveragePct >= 80 ? 'good' : 'warn',
          },
          {
            label: 'Tanpa nama',
            value: fmtInt(t.unassigned),
            note: t.unassigned > 0 ? 'tidak ada yang bisa ditanyai' : 'semua ada penanggung jawabnya',
            tone: t.unassigned > 0 ? 'crit' : 'good',
          },
          {
            label: 'Orang aktif',
            value: fmtInt(t.people),
            note: t.overloaded > 0 ? `${t.overloaded} kelebihan beban` : undefined,
            tone: t.overloaded > 0 ? 'warn' : undefined,
          },
        ]}
      />

      {t.activities === 0 && (
        <div className="banner">
          Tidak ada aktivitas yang berjalan pada minggu ini menurut versi programme yang diterima.
          Kalau itu mengejutkan, periksa versi programme di prog. lebih dulu — papan ini tidak punya
          sumber lain.
        </div>
      )}

      {t.activities > 0 && board.people.length === 0 && (
        <div className="banner">
          Tidak satu pun dari {fmtInt(t.activities)} aktivitas minggu ini punya nama orang. Programme
          menyebut kru, dan kru belum dipetakan ke siapa pun, jadi papan per orang di bawah kosong
          bukan karena tidak ada pekerjaan.
        </div>
      )}

      <Panel
        title="Per orang"
        note="Diurutkan menurut beban. Yang paling banyak dipegang lebih dulu, karena itu yang paling dulu jatuh."
        wide
      >
        {board.people.length === 0 ? (
          <p className="hint">Belum ada orang yang memegang pekerjaan minggu ini.</p>
        ) : (
          board.people.map((p) => (
            <div className="rev" key={p.personId}>
              <button
                className="rev-head"
                onClick={() => setOpen(open === p.personId ? null : p.personId)}
                aria-expanded={open === p.personId}
              >
                <span className="num" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  {p.name}
                </span>
                {p.role && <span className="chip">{p.role}</span>}
                {p.lateCount > 0 && <span className="chip crit">{p.lateCount} lewat tanggal</span>}
                <span className="lmeta" style={{ marginLeft: 'auto' }}>
                  {p.org ?? 'organisasi belum diisi'} · {p.load} aktivitas
                </span>
              </button>
              {open === p.personId && (
                <div className="rev-body">
                  <Activities rows={p.activities} />
                </div>
              )}
            </div>
          ))
        )}
      </Panel>

      <Panel
        title="Tanpa nama"
        note={
          t.unassigned === 0
            ? 'Setiap aktivitas minggu ini punya orang yang bisa ditanyai.'
            : 'Aktivitas ini berjalan minggu ini dan tidak ada orang yang bertanggung jawab atasnya.'
        }
        wide
      >
        {board.unassigned.length === 0 ? (
          <p className="hint">Tidak ada.</p>
        ) : (
          <>
            <Activities rows={board.unassigned} />
            {t.unassigned > board.unassigned.length && (
              <p className="panel-foot">
                Menampilkan {fmtInt(board.unassigned.length)} dari {fmtInt(t.unassigned)}. Sisanya
                tidak dikirim server — memetakan kru ke orang memperkecil daftar ini lebih cepat
                daripada membacanya sampai habis.
              </p>
            )}
          </>
        )}
      </Panel>
    </>
  );
}
